// Team Planner — page shell. Top header (BSP-themed icon + title +
// + New / notification / help), stats strip, tab bar + scope selector,
// then the active view below. The unified create slide-over is mounted
// at this level via PlannerCreateProvider so every descendent (cards,
// command palette, kanban "+ New" buttons) can open it.
import { useEffect, useState } from "react";
import { BellRing, HelpCircle, LayoutDashboard, MessageSquare, Plus, ShieldAlert } from "lucide-react";
import { useAuth } from "../../../auth/useAuth";
import { TabBar } from "./components/TabBar";
import { StatsStrip } from "./components/StatsStrip";
import { ScopeSelector } from "./components/ScopeSelector";
import { CommandPalette } from "./components/CommandPalette";
import { tpApi } from "./api";
import { useResource } from "./hooks";
import { MyWorkView } from "./views/MyWorkView";
import { RocksView } from "./views/RocksView";
import { MilestonesView } from "./views/MilestonesView";
import { TodosView } from "./views/TodosView";
import { OpportunitiesView } from "./views/OpportunitiesView";
import { TeamsView } from "./views/TeamsView";
import { HealthView } from "./views/HealthView";
import { PlannerCreateProvider, usePlannerCreate } from "./sheets/PlannerCreateContext";
import { NotificationPreferencesPanel } from "./sheets/NotificationPreferencesPanel";
import { GraphDiagPanel } from "./sheets/GraphDiagPanel";
import { BotDiagPanel } from "./sheets/BotDiagPanel";
import { RockSheet } from "./sheets/RockSheet";
import { TodoSheet } from "./sheets/TodoSheet";
import { IssueSheet } from "./sheets/IssueSheet";
import { currentQuarter } from "./utils";
import type {
  PlannerScopeId,
  PlannerViewId,
} from "../../../../../shared/team-planner/const";

function NewMenuButton() {
  const openCreate = usePlannerCreate();
  return (
    <button
      type="button"
      className="bsp-btn bsp-btn-primary"
      onClick={() => openCreate("rock")}
      title="Create — switch with R / M / T / O"
    >
      <Plus size={14} /> New
    </button>
  );
}

function TeamPlannerInner() {
  const [view, setView] = useState<PlannerViewId>("my-work");
  const [scope, setScope] = useState<PlannerScopeId>("mine");
  const ctx = useResource(() => tpApi.meContext(), []);
  const { isAdmin } = useAuth();
  const openCreate = usePlannerCreate();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [botDiagOpen, setBotDiagOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Palette-opened item drives an overlay sheet directly so a user can
  // jump into a rock/todo/issue without first navigating to its view.
  const [paletteOpenItem, setPaletteOpenItem] = useState<
    { kind: "rock" | "todo" | "issue"; id: string } | null
  >(null);

  // Global Cmd/Ctrl+K opens the palette. Bare letter hotkeys are
  // handled by the create slide-over; we don't compete for them here.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Notification CTA deep-links — every email + Teams card carries a
  // ?rock=<id> / ?todo=<id> / ?opp=<id> query param built by
  // server/services/team-planner/notify.ts. Open the matching sheet
  // on first render so clicking the card actually lands on the item,
  // then strip the param via history.replaceState so a refresh
  // doesn't keep re-opening it. (Codex PR #41 P2.)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rockId = params.get("rock");
    const todoId = params.get("todo");
    const oppId = params.get("opp");
    const milestoneId = params.get("milestone");
    const uuid = /^[0-9a-f-]{36}$/i;
    let consumed = false;
    if (rockId && uuid.test(rockId)) {
      setView("rocks");
      setPaletteOpenItem({ kind: "rock", id: rockId });
      consumed = true;
    } else if (todoId && uuid.test(todoId)) {
      setView("todos");
      setPaletteOpenItem({ kind: "todo", id: todoId });
      consumed = true;
    } else if (oppId && uuid.test(oppId)) {
      setView("opportunities");
      setPaletteOpenItem({ kind: "issue", id: oppId });
      consumed = true;
    } else if (milestoneId && uuid.test(milestoneId)) {
      // Land on the cross-rock Milestones kanban — the user can find
      // their milestone there. We don't open a dedicated sheet because
      // milestones are edited inline on the parent rock's pane.
      setView("milestones");
      consumed = true;
    }
    if (consumed) {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("rock");
      cleaned.searchParams.delete("todo");
      cleaned.searchParams.delete("opp");
      cleaned.searchParams.delete("milestone");
      window.history.replaceState({}, "", cleaned.toString());
    }
  }, []);

  // Clamp the scope down if the viewer's role can't see ALL — guards
  // a refresh after a role change.
  useEffect(() => {
    if (scope === "all" && ctx.data && !ctx.data.canSeeAllScope) {
      setScope("mine");
    }
  }, [scope, ctx.data]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header className="tp-page-header bsp-page-header" style={{ marginBottom: 0 }}>
        <div className="tp-page-icon">
          <LayoutDashboard size={18} />
        </div>
        <h1 className="bsp-page-title" style={{ flex: 1 }}>
          Team Planner
        </h1>
        <NewMenuButton />
        <button
          type="button"
          className="tp-icon-action"
          aria-label="Notification preferences"
          title="Notification preferences"
          onClick={() => setPrefsOpen(true)}
        >
          <BellRing size={14} />
        </button>
        {isAdmin ? (
          <>
            <button
              type="button"
              className="tp-icon-action"
              aria-label="Graph integration diagnostics"
              title="Graph integration diagnostics (admin)"
              onClick={() => setDiagOpen(true)}
            >
              <ShieldAlert size={14} />
            </button>
            <button
              type="button"
              className="tp-icon-action"
              aria-label="Notification (Teams + email) diagnostics"
              title="Notification diagnostics (admin)"
              onClick={() => setBotDiagOpen(true)}
            >
              <MessageSquare size={14} />
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="tp-icon-action"
          aria-label="Command palette (Cmd+K)"
          title="Command palette · ⌘K"
          onClick={() => setPaletteOpen(true)}
        >
          <HelpCircle size={14} />
        </button>
      </header>

      <StatsStrip />

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <TabBar active={view} onSelect={setView} />
        <ScopeSelector
          value={scope}
          onChange={setScope}
          canSeeAllScope={ctx.data?.canSeeAllScope ?? false}
        />
      </div>

      {view === "my-work" ? <MyWorkView onNavigate={setView} /> : null}
      {view === "rocks" ? <RocksView scope={scope} /> : null}
      {view === "milestones" ? <MilestonesView scope={scope} /> : null}
      {view === "todos" ? <TodosView scope={scope} /> : null}
      {view === "opportunities" ? <OpportunitiesView /> : null}
      {view === "health" ? <HealthView scope={scope === "all" ? "all" : "mine"} /> : null}
      {view === "teams" ? <TeamsView /> : null}

      <NotificationPreferencesPanel open={prefsOpen} onClose={() => setPrefsOpen(false)} />
      <GraphDiagPanel open={diagOpen} onClose={() => setDiagOpen(false)} />
      <BotDiagPanel open={botDiagOpen} onClose={() => setBotDiagOpen(false)} />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={setView}
        onCreate={(k) => openCreate(k)}
        onOpenItem={(kind, id) => setPaletteOpenItem({ kind, id })}
      />
      {paletteOpenItem?.kind === "rock" ? (
        <RockSheet
          id={paletteOpenItem.id}
          quarter={currentQuarter()}
          onClose={() => setPaletteOpenItem(null)}
          onChanged={() => undefined}
        />
      ) : null}
      {paletteOpenItem?.kind === "todo" ? (
        <TodoSheet
          id={paletteOpenItem.id}
          onClose={() => setPaletteOpenItem(null)}
          onChanged={() => undefined}
        />
      ) : null}
      {paletteOpenItem?.kind === "issue" ? (
        <IssueSheet
          id={paletteOpenItem.id}
          onClose={() => setPaletteOpenItem(null)}
          onChanged={() => undefined}
        />
      ) : null}
    </div>
  );
}

export function TeamPlannerPage() {
  return (
    <PlannerCreateProvider>
      <TeamPlannerInner />
    </PlannerCreateProvider>
  );
}
